import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth.js";
import type { LessonCreditSummary } from "../booking/api.js";
import { cancelLessonBlock } from "../booking/CreditCorrections.js";
import { recentAmeliaPurchaseMessage } from "../booking/creditSync.js";
import { tutorLessonBlockSourceLabel } from "../booking/lessonBlockSaleModel.js";
import {
  staffBooking,
  type LessonBlockCouponInfo,
  type LessonBlockList,
  type LessonBlockProduct,
} from "../booking/staff.js";
import { TutorAddCredits } from "../booking/TutorAddCredits.js";
import {
  registrationSettledSummary,
  registrationSettlementWarning,
  type RegistrationSettledSummary,
} from "../booking/registrationReconciliation.js";
import { TutorBookLesson } from "../booking/TutorBookLesson.js";
import { MessageFamilyButton } from "../inbox/Inbox.js";
import { formatDate } from "../format.js";
import { Pagination } from "../Pagination.js";
import { DENSE_ROW_PAGE_SIZE } from "../paginationPageSizes.js";
import { useAsync } from "../useAsync.js";
import { useStudentCreditReads } from "../booking/StudentCreditReads.js";

const PAGE_SIZE = DENSE_ROW_PAGE_SIZE;

export function canAddTutorCredits(
  capabilities: readonly string[],
  providerId: number | null,
): boolean {
  const held = new Set(capabilities);
  return providerId !== null && !held.has("lesson_blocks.manage.all");
}

function couponDeliveryLabel(coupon: LessonBlockCouponInfo | null): string {
  if (coupon === null) return "No coupon on this block";
  const delivery = coupon.delivery;
  if (delivery === null) return "No delivery attempt recorded";
  switch (delivery.status) {
    case "sent":
      return `Sent to ${delivery.recipient_email}`;
    case "pending":
      return `Not sent yet (queued for ${delivery.recipient_email})`;
    case "retryable":
      return `Delivery failed once, retrying — ${delivery.recipient_email}`;
    case "requires_attention":
      return `Delivery failed and needs a look — ${delivery.recipient_email}${
        delivery.last_error ? ` (${delivery.last_error})` : ""
      }`;
    default:
      return delivery.status;
  }
}

export function TutorStudentTools({
  studentId,
  studentName,
  showBlockDetails,
  showBooking,
  onChanged,
}: {
  studentId: string;
  studentName: string;
  showBlockDetails: boolean;
  showBooking: boolean;
  onChanged: () => void;
}) {
  const { me } = useAuth();
  const creditReads = useStudentCreditReads();
  const ownProviderId = me?.booking.providerId ?? null;
  const canAddFiveCredits = canAddTutorCredits(
    me?.capabilities ?? [],
    ownProviderId,
  );
  const publish = usePublishActionOutcome();
  function report(result: ActionOutcome) { if (!publish(result)) setOutcome(result); }
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [registrationWarning, setRegistrationWarning] = useState<string | null>(null);
  const [registrationSummary, setRegistrationSummary] = useState<RegistrationSettledSummary | null>(null);
  const [creditPage, setCreditPage] = useState(1);
  const [creditSnapshot, setCreditSnapshot] = useState<LessonCreditSummary | null>(null);
  const [ledgerSnapshot, setLedgerSnapshot] = useState<LessonBlockList | null>(null);
  const [couponChecks, setCouponChecks] = useState<
    Record<string, "loading" | "error" | { coupon: LessonBlockCouponInfo | null }>
  >({});
  const [closingBlockId, setClosingBlockId] = useState<string | null>(null);
  const [closeReason, setCloseReason] = useState("");
  const [closeProblem, setCloseProblem] = useState<string | null>(null);
  const [closeBusy, setCloseBusy] = useState(false);

  useEffect(() => {
    setShowAddCredits(false);
    setOutcome(null);
    setRegistrationWarning(null);
    setRegistrationSummary(null);
    setCreditPage(1);
    setCreditSnapshot(null);
    setLedgerSnapshot(null);
    setCouponChecks({});
    setClosingBlockId(null);
    setCloseReason("");
    setCloseProblem(null);
    setCloseBusy(false);
  }, [studentId, showBlockDetails]);

  useEffect(() => {
    setCreditSnapshot(null);
    setLedgerSnapshot(null);
  }, [creditReads.revision]);

  const credits = useAsync(
    () => creditReads.lessonCredits(studentId),
    [studentId, creditReads],
  );
  const creditLedger = useAsync<LessonBlockList | null>(
    () =>
      showBlockDetails
        ? creditReads.lessonBlocks(studentId)
        : Promise.resolve(null),
    [studentId, showBlockDetails, creditReads],
  );
  const blockProducts = useAsync<LessonBlockProduct[]>(
    () =>
      canAddFiveCredits
        ? staffBooking.lessonBlockProducts()
        : Promise.resolve([]),
    [canAddFiveCredits],
  );

  const effectiveCredits = creditSnapshot ?? credits.data;
  const effectiveLedger = ledgerSnapshot ?? creditLedger.data;
  const creditData =
    effectiveCredits?.portal_student_id === studentId ? effectiveCredits : null;
  const sourceByBlockId = useMemo(
    () =>
      new Map(
        (effectiveLedger?.blocks ?? []).map((detail) => [
          detail.block.id,
          detail.block.source,
        ]),
      ),
    [effectiveLedger],
  );

  const creditBlocks = creditData
    ? creditData.blocks.slice(
        (creditPage - 1) * PAGE_SIZE,
        creditPage * PAGE_SIZE,
      )
    : [];
  const showCreditPanel = showBlockDetails || canAddFiveCredits;

  async function checkCouponDelivery(lessonBlockId: string) {
    setCouponChecks((prior) => ({ ...prior, [lessonBlockId]: "loading" }));
    try {
      const detail = await staffBooking.lessonBlockDetail(lessonBlockId);
      setCouponChecks((prior) => ({
        ...prior,
        [lessonBlockId]: { coupon: detail.coupon },
      }));
    } catch {
      setCouponChecks((prior) => ({ ...prior, [lessonBlockId]: "error" }));
    }
  }

  async function refreshAuthoritativeCredits() {
    creditReads.invalidate();
    const [nextCredits, nextLedger] = await Promise.all([
      creditReads.lessonCredits(studentId),
      showBlockDetails
        ? creditReads.lessonBlocks(studentId)
        : Promise.resolve(null),
    ]);
    if (nextCredits.portal_student_id !== studentId) {
      throw new Error("The refreshed credit response did not match this student.");
    }
    setCreditSnapshot(nextCredits);
    setLedgerSnapshot(nextLedger);
    return nextCredits;
  }

  async function applyFiveCredits(
    mutation: () => Promise<import("../booking/staff.js").LessonBlockDetail>,
  ): Promise<void> {
    const detail = await mutation();
    const warning = registrationSettlementWarning(detail);
    // This is evidence from the accepted mutation, independent of the later
    // authoritative balance read. An empty same-block replay must retain it.
    const settled = registrationSettledSummary(detail, registrationSummary);
    setRegistrationSummary((prior) => registrationSettledSummary(detail, prior));
    try {
      const refreshed = await refreshAuthoritativeCredits();
      report({ variant: warning ? "attention" : "success", title: "Lesson block added", description: studentName,
        details: [{ value: "5 lesson credits registered" }, ...(Number.isFinite(refreshed.total_available_credits) ? [{ label: "New balance", value: `${refreshed.total_available_credits} credits` }] : []), ...(warning ? [{ value: warning.message }] : []), ...(settled ? [{ value: <>{settled.heading}<ul>{settled.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}</ul>{settled.more > 0 && <p>and {settled.more} more</p>}</> }] : [])] });
    } catch (cause: unknown) {
      const detail = cause instanceof Error ? ` ${cause.message}` : "";
      throw new Error(
        `${warning?.retryable ? `${warning.message} ` : ""}The credits were accepted, but the authoritative credit state could not be re-read. Retry this same addition before starting another.${detail}`,
      );
    }
    if (warning?.retryable) {
      setRegistrationWarning(null);
      throw new Error(warning.message);
    }
    setRegistrationWarning(warning?.message ?? null);
    setShowAddCredits(false);
    onChanged();
  }

  async function confirmBlockCancellation() {
    if (closingBlockId === null || closeBusy) return;
    const reason = closeReason.trim();
    setCloseBusy(true);
    setCloseProblem(null);
    try {
      await cancelLessonBlock(closingBlockId, reason, crypto.randomUUID());
      const refreshed = await refreshAuthoritativeCredits();
      report({ variant: "success", title: "Lesson block cancelled", description: studentName,
        details: [...(Number.isFinite(refreshed.total_available_credits) ? [{ label: "Remaining lesson balance", value: `${refreshed.total_available_credits} credits` }] : [])] });
      setClosingBlockId(null);
      setCloseReason("");
      onChanged();
    } catch (cause: unknown) {
      setCloseProblem(
        cause instanceof Error ? cause.message : "Could not cancel this lesson block.",
      );
    } finally {
      setCloseBusy(false);
    }
  }

  function bookingChanged() {
    creditReads.invalidate();
    setCreditSnapshot(null);
    setLedgerSnapshot(null);
    credits.reload();
    creditLedger.reload();
    onChanged();
  }

  return (
    <>
      <MessageFamilyButton studentId={studentId} studentName={studentName} />

      {showCreditPanel && (
        <>
          <h2>Lesson credits</h2>
          {outcome && <ActionConfirmation {...outcome} reveal />}
          {(registrationWarning || registrationSummary) && <ActionConfirmation variant={registrationWarning ? "attention" : "success"} title="Lesson block settlement" details={[{ value: <>
            {registrationSummary && <>
              <p><strong>{registrationSummary.heading}</strong></p>
              <ul>
                {registrationSummary.lessons.map((lesson, index) => <li key={index}>{lesson}</li>)}
              </ul>
              {registrationSummary.more > 0 && <p>and {registrationSummary.more} more</p>}
            </>}
            {registrationWarning && <p>{registrationWarning}</p>}
          </> }]} />}
          {credits.loading && !creditData && (
            <p className="empty">Loading credit availability…</p>
          )}
          {credits.error && !creditSnapshot && (
            <div className="error">
              We could not load this student&rsquo;s credit availability right now.
            </div>
          )}
          {effectiveCredits !== null && !creditData && !credits.loading && !credits.error && (
            <div className="error">
              The credit response did not match the selected student.
            </div>
          )}
          {creditData && (
            <div className="panel">
              <div className="spread-center">
                <div>
                  <div className="small muted">Lesson credits</div>
                  <strong>{creditData.total_available_credits} remaining</strong>
                </div>
                {canAddFiveCredits && !showAddCredits && (
                  <button
                    type="button"
                    onClick={() => setShowAddCredits(true)}
                    disabled={blockProducts.loading || blockProducts.error !== null}
                  >
                    Add 5 credits
                  </button>
                )}
              </div>

              {canAddFiveCredits && blockProducts.error && (
                <div className="error small" style={{ marginTop: 10 }}>
                  We could not load the configured 5-credit products right now.
                </div>
              )}
              {showAddCredits &&
                blockProducts.data &&
                ownProviderId !== null && (
                  <TutorAddCredits
                    studentId={studentId}
                    studentName={studentName}
                    ownProviderId={ownProviderId}
                    products={blockProducts.data}
                    onCancel={() => setShowAddCredits(false)}
                    onApply={applyFiveCredits}
                  />
                )}

              {showBlockDetails && creditData.blocks.length === 0 && (
                <div className="notice" style={{ marginTop: 12 }}>
                  <strong>No lesson blocks are showing for this student yet</strong>
                  <div className="small muted">{recentAmeliaPurchaseMessage}</div>
                </div>
              )}

              {showBlockDetails && creditData.blocks.length > 0 && (
                <>
                  {closeProblem && <div className="error small" style={{ marginTop: 12 }}>{closeProblem}</div>}
                  <table style={{ marginTop: 12 }}>
                    <thead>
                      <tr>
                        <th>Lesson block</th>
                        <th>Available</th>
                        <th>Expires</th>
                        <th>Source</th>
                        <th>Status</th>
                        <th>Coupon</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditBlocks.map((block) => {
                        const check = couponChecks[block.lesson_block_id];
                        const source = sourceByBlockId.get(block.lesson_block_id);
                        const cancellable =
                          source === "staff" &&
                          (block.status === "active" || block.status === "requires_attention");
                        return (
                          <tr key={block.lesson_block_id}>
                            <td>{block.service_name}</td>
                            <td className="tabular">{block.available_credits}</td>
                            <td className="muted small">
                              {block.expires_at
                                ? formatDate(block.expires_at)
                                : "No expiry"}
                            </td>
                            <td className="muted small">
                              {source === undefined ? "—" : tutorLessonBlockSourceLabel(source)}
                            </td>
                            <td className="muted small">{block.status}</td>
                            <td className="small">
                              {check === undefined && (
                                <button
                                  className="quiet small"
                                  type="button"
                                  onClick={() =>
                                    void checkCouponDelivery(block.lesson_block_id)
                                  }
                                >
                                  Check delivery
                                </button>
                              )}
                              {check === "loading" && (
                                <span className="muted">Checking…</span>
                              )}
                              {check === "error" && (
                                <span className="error">Could not check right now.</span>
                              )}
                              {check !== undefined &&
                                check !== "loading" &&
                                check !== "error" && (
                                  <span className="muted">
                                    {couponDeliveryLabel(check.coupon)}
                                  </span>
                                )}
                            </td>
                            <td className="small">
                              {cancellable && closingBlockId !== block.lesson_block_id && (
                                <button
                                  className="quiet small"
                                  type="button"
                                  disabled={closeBusy}
                                  onClick={() => {
                                    setClosingBlockId(block.lesson_block_id);
                                    setCloseReason("");
                                    setCloseProblem(null);
                                  }}
                                >
                                  Cancel block
                                </button>
                              )}
                              {cancellable && closingBlockId === block.lesson_block_id && (
                                <div className="stack">
                                  <label>
                                    <span className="small muted">Note (optional)</span>
                                    <textarea
                                      value={closeReason}
                                      maxLength={2000}
                                      disabled={closeBusy}
                                      onChange={(event) => setCloseReason(event.target.value)}
                                    />
                                  </label>
                                  <div className="row">
                                    <button
                                      type="button"
                                      disabled={closeBusy}
                                      onClick={() => void confirmBlockCancellation()}
                                    >
                                      {closeBusy ? "Cancelling…" : "Confirm cancel"}
                                    </button>
                                    <button
                                      className="quiet small"
                                      type="button"
                                      disabled={closeBusy}
                                      onClick={() => {
                                        setClosingBlockId(null);
                                        setCloseReason("");
                                        setCloseProblem(null);
                                      }}
                                    >
                                      Keep block
                                    </button>
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <Pagination
                    page={creditPage}
                    pages={Math.max(1, Math.ceil(creditData.blocks.length / PAGE_SIZE))}
                    ariaLabel="Lesson credit blocks pagination"
                    onPageChange={setCreditPage}
                  />
                </>
              )}
            </div>
          )}
        </>
      )}

      {showBooking && creditData && (
        <TutorBookLesson
          studentId={studentId}
          studentName={studentName}
          blocks={creditData.blocks}
          selectedTutorProviderId={ownProviderId}
          onBooked={bookingChanged}
        />
      )}
    </>
  );
}
